import React, { useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { Player } from 'src/types';
import { axiosPost } from 'src/utils/httpClient';

import s from './style.module.scss';

const dialogStyles = {
  backgroundColor: '#333A53',
  color: 'white',
  padding: '25px',
  borderRadius: '24px',
  boxShadow: 'none',
};

interface PlayerEditModalProps {
  open: boolean;
  handleClose: () => void;
  player: Player;
  onPlayerUpdate: (updatedPlayer: Player) => void;
}

interface PlayerUpdateRequest {
  uuid: string;
  discordName?: string;
  kickName?: string;
  shuffleName?: string;
  csgoBigId?: string;
  empireDropId?: string;
}

const updatePlayerInfo = async (
  request: PlayerUpdateRequest,
): Promise<Player> => axiosPost(`/admin/update-player-info`, request);

const PlayerEditModal = ({
  open,
  handleClose,
  player,
  onPlayerUpdate,
}: PlayerEditModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    discordName: player.discordName || '',
    kickName: player.kickName || '',
    shuffleName: player.shuffleName || '',
    csgoBigId: player.csgoBigId || '',
    empireDropId: player.empireDropId || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const updatedPlayer = await updatePlayerInfo({
        uuid: player.id,
        ...formData,
      });
      onPlayerUpdate(updatedPlayer);
      toast.success('Player information updated successfully');
      handleClose();
    } catch (error) {
      toast.error('Failed to update player information');
      console.error('Error updating player:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      className={s.modal}
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: dialogStyles }}
    >
      <Box className={s.modal__header}>
        <Typography variant="h5">Edit Player Information</Typography>
        <Button className={s.modal__closeButton} onClick={handleClose}>
          <CloseIcon />
        </Button>
      </Box>

      <Box className={s.modal__content}>
        <TextField
          fullWidth
          label="Discord Name"
          name="discordName"
          value={formData.discordName}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          className={s.modal__textField}
        />
        <TextField
          fullWidth
          label="Kick Name"
          name="kickName"
          value={formData.kickName}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          className={s.modal__textField}
        />
        <TextField
          fullWidth
          label="Shuffle Name"
          name="shuffleName"
          value={formData.shuffleName}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          className={s.modal__textField}
        />
        <TextField
          fullWidth
          label="CSGO Big User ID"
          name="csgoBigId"
          value={formData.csgoBigId}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          className={s.modal__textField}
        />
        <TextField
          fullWidth
          label="Empire Drop User ID"
          name="empireDropId"
          value={formData.empireDropId}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          className={s.modal__textField}
        />
      </Box>

      <Box className={s.modal__actions}>
        <Button
          variant="contained"
          color="error"
          onClick={handleClose}
          className={s.modal__button}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isLoading}
          className={s.modal__button}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default PlayerEditModal;
